/**
 * 实体卡绑定 & 激活页面（两步流程）
 * Step 1: 绑定卡片 - 输入卡号、有效期、CVV → 调用 bindExistingCardV2
 * Step 2: 激活卡片 - 显示卡片预览 + 输入激活码 → 调用 activatePhysicalCardV2
 * 成功后弹出激活成功 Popup
 *
 * 入口：
 * - 从 PendingCard "激活卡片" 按钮进入（传 id、backgroundImage、cardBin）
 * - 从 bind-existing-card 选持卡人后跳转（传 kycId、productId、product 等）
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import CustomTextInput from '@/components/CustomTextInput'
import CustomButton from '@/components/CustomButton'
import FixedBottom from '@/components/FixedBottom'
import CardView from '../../components/card-view'
import { Popup, Toast } from 'react-vant'
import { APIWeb } from '@/api'

const enum Step {
    BindCard = 1,
    ActivateCard = 2,
}

interface LocationState {
    /** 卡片背景图 */
    backgroundImage?: string
    /** 卡BIN（4位卡头） */
    cardBin?: string
    /** 持卡人KYC ID（从绑定已有卡流程传入） */
    kycId?: number
    /** 卡产品ID（从绑定已有卡流程传入） */
    productId?: number
    /** 原始产品数据（从绑定已有卡流程传入） */
    product?: ModelsCardProduct
    /** 申请ID（从绑定已有卡流程传入） */
    applicationId?: number
    /** 卡产品类型(0虚拟卡，1是wasabi实体卡,2是sunrate实体卡) */
    activeType?: number
}

export default React.memo(function ActivateCard() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const locationState = location.state as LocationState | null

    // 从 URL 或 state 获取参数
    const backgroundImage = locationState?.backgroundImage || (locationState?.product as any)?.image || '/images/card-v2/electronic-card/zise-card-1.png'

    // 激活码最大长度：wasabi(1)=8位邮箱验证码，sunrate(2)=4位ATM密码，默认10位
    const activationCodeMaxLength = locationState?.activeType === 1 ? 8 : locationState?.activeType === 2 ? 4 : 10

    // 当前步骤
    const [step, setStep] = useState<Step>(Step.BindCard)

    // Step 1: 绑定卡片表单
    const [cardNumber, setCardNumber] = useState('')
    const [expireDate, setExpireDate] = useState('')
    const [cvv, setCvv] = useState('')
    const [bindLoading, setBindLoading] = useState(false)

    // Step 2: 激活码 / ATM 密码（type2 需确认）
    const [activationCode, setActivationCode] = useState('')
    const [atmPinConfirm, setAtmPinConfirm] = useState('')
    const [activateLoading, setActivateLoading] = useState(false)

    // activeType===1 进入激活步时展示 KYC 邮箱验证码提示
    const [kycEmailReminderVisible, setKycEmailReminderVisible] = useState(false)

    // 激活成功弹窗
    const [successPopupVisible, setSuccessPopupVisible] = useState(false)

    useEffect(() => {
        if (step === Step.ActivateCard && locationState?.activeType === 1) {
            setKycEmailReminderVisible(true)
        }
    }, [step, locationState?.activeType])

    // 从 step 1 保存的卡号（用于 step 2 显示和 API 调用）
    const [boundCardNumber, setBoundCardNumber] = useState('')

    // 掩码卡号（用于 step 2 的卡片预览展示）
    const maskedCardNumber = useMemo(() => {
        if (!boundCardNumber) return ''
        const digits = boundCardNumber.replace(/\D/g, '')
        if (digits.length < 4) return digits
        return `${digits.slice(0, 4)} **** **** ****`
    }, [boundCardNumber])

    // 格式化有效期输入 (自动加 /)
    const handleExpireDateChange = useCallback((text: string) => {
        // 移除非数字字符
        let digits = text.replace(/\D/g, '')
        if (digits.length > 4) digits = digits.slice(0, 4)
        // 自动格式化为 MM/YY
        if (digits.length >= 3) {
            setExpireDate(`${digits.slice(0, 2)}/${digits.slice(2)}`)
        } else {
            setExpireDate(digits)
        }
    }, [])

    // Step 1 表单验证
    const canBind = useMemo(() => {
        const cardDigits = cardNumber.replace(/\D/g, '')
        const expireDigits = expireDate.replace(/\D/g, '')
        return cardDigits.length >= 13 && expireDigits.length === 4 && cvv.trim().length >= 3
    }, [cardNumber, expireDate, cvv])

    // Step 1: 提交绑定
    const handleBind = useCallback(async () => {
        if (!canBind) return
        
        setBindLoading(true)
        try {
            await APIWeb.bindExistingCardV2({
                card_number: cardNumber.replace(/\D/g, ''),
                expire_date: expireDate,
                cvv: cvv.trim(),
                product_id: locationState?.productId,
                kyc_id: locationState?.kycId,
                application_id: locationState?.applicationId,
            })
            
            // 绑定成功，保存卡号并进入激活步骤
            setBoundCardNumber(cardNumber.replace(/\D/g, ''))
            setActivationCode('')
            setAtmPinConfirm('')
            setStep(Step.ActivateCard)
        } catch (error: any) {
            console.error('绑定卡片失败:', error)
        } finally {
            setBindLoading(false)
        }
    }, [canBind, cardNumber, expireDate, cvv])

    const canSubmitActivate = useMemo(() => {
        const at = locationState?.activeType
        if (at === 1) {
            return activationCode.replace(/\D/g, '').length === 8
        }
        if (at === 2) {
            const a = activationCode.replace(/\D/g, '')
            const b = atmPinConfirm.replace(/\D/g, '')
            return a.length === 4 && b.length === 4
        }
        return activationCode.trim().length >= activationCodeMaxLength
    }, [locationState?.activeType, activationCode, atmPinConfirm, activationCodeMaxLength])

    // Step 2: 提交激活
    const handleActivate = useCallback(async () => {
        if (!canSubmitActivate) return

        const at = locationState?.activeType
        if (at === 2) {
            const a = activationCode.replace(/\D/g, '')
            const b = atmPinConfirm.replace(/\D/g, '')
            if (a !== b) {
                Toast.fail(t('输入的两次密码不一致'))
                return
            }
        }

        const code =
            at === 1 || at === 2
                ? activationCode.replace(/\D/g, '')
                : activationCode.trim()

        setActivateLoading(true)
        try {
            await APIWeb.activatePhysicalCardV2({
                pan: boundCardNumber,
                activation_code: code,
                cvv: cvv.trim(),
                expire_date: expireDate,
            })

            // 激活成功，显示弹窗
            setSuccessPopupVisible(true)
        } catch (error: any) {
            console.error('激活卡片失败:', error)
        } finally {
            setActivateLoading(false)
        }
    }, [
        canSubmitActivate,
        boundCardNumber,
        activationCode,
        atmPinConfirm,
        expireDate,
        cvv,
        locationState?.activeType,
        t,
    ])

    // 激活成功后关闭弹窗
    const handleSuccessConfirm = useCallback(() => {
        setSuccessPopupVisible(false)
        navigate('/card', { replace: true })
    }, [navigate])

    // 返回按钮逻辑
    const handleBack = useCallback(() => {
        if (step === Step.ActivateCard) {
            setActivationCode('')
            setAtmPinConfirm('')
            setStep(Step.BindCard)
        }
    }, [step])

    // Step 1: 绑定卡片
    const renderBindCard = () => (
        <>
            <div className="px-5 pt-6 pb-24">
                {/* 卡号 */}
                <div className="mb-6">
                    <div className="text-sm text-[#667085] font-medium mb-2">{t('卡号')}</div>
                    <CustomTextInput
                        value={cardNumber}
                        onChangeText={(text) => setCardNumber(text.replace(/\D/g, '').slice(0, 19))}
                        placeholder={t('输入您的卡号')}
                        inputMode="numeric"
                        maxLength={19}
                    />
                </div>

                {/* 有效期 */}
                <div className="mb-6">
                    <div className="text-sm text-[#667085] font-medium mb-2">{t('有效期')}</div>
                    <CustomTextInput
                        value={expireDate}
                        onChangeText={handleExpireDateChange}
                        placeholder="MM/YY"
                        inputMode="numeric"
                        maxLength={5}
                    />
                </div>

                {/* 安全码 */}
                <div className="mb-6">
                    <div className="text-sm text-[#667085] font-medium mb-2">{t('安全码')}</div>
                    <CustomTextInput
                        value={cvv}
                        onChangeText={(text) => setCvv(text.replace(/\D/g, '').slice(0, 4))}
                        placeholder="CVV"
                        inputMode="numeric"
                        maxLength={4}
                    />
                </div>
            </div>

            <FixedBottom className="bg-white">
                <CustomButton
                    onClick={handleBind}
                    disabled={!canBind}
                    loading={bindLoading}
                >
                    {t('下一步')}
                </CustomButton>
            </FixedBottom>
        </>
    )

    // Step 2: 激活卡片
    const renderActivateCard = () => (
        <>
            <div className="px-5 pt-4 pb-24">
                {/* 卡片预览 */}
                <CardView
                    cardStatus="pending"
                    backgroundImage={backgroundImage}
                    maskedPan={maskedCardNumber}
                />

                {/* 验证码 / ATM 取现密码 */}
                <div className="mt-6">
                    {locationState?.activeType === 2 ? (
                        <>
                            <div className="text-sm text-[#667085] font-medium mb-2">
                                {t('设置4位ATM取现密码')}
                            </div>
                            <CustomTextInput
                                value={activationCode}
                                onChangeText={(text) =>
                                    setActivationCode(text.replace(/\D/g, '').slice(0, 4))
                                }
                                type="password"
                                placeholder={t('输入密码')}
                                maxLength={4}
                                inputMode="numeric"
                                secureTextEntry
                                isShowSmallEyes
                            />
                            <div className="text-sm text-[#667085] font-medium mb-2 mt-4">
                                {t('确认密码')}
                            </div>
                            <CustomTextInput
                                value={atmPinConfirm}
                                onChangeText={(text) =>
                                    setAtmPinConfirm(text.replace(/\D/g, '').slice(0, 4))
                                }
                                type="password"
                                placeholder={t('再次输入密码')}
                                maxLength={4}
                                inputMode="numeric"
                                secureTextEntry
                                isShowSmallEyes
                            />
                        </>
                    ) : (
                        <>
                            {locationState?.activeType === 1 ? (
                                <div className="text-sm text-[#667085] font-medium mb-2">
                                    {t('8位数验证码')}
                                </div>
                            ) : null}
                            <CustomTextInput
                                value={activationCode}
                                onChangeText={(text) => {
                                    const digits = text.replace(/\D/g, '')
                                    const max = activationCodeMaxLength
                                    setActivationCode(digits.slice(0, max))
                                }}
                                placeholder={t('输入验证码')}
                                maxLength={activationCodeMaxLength}
                                inputMode={locationState?.activeType === 1 ? 'numeric' : undefined}
                                secureTextEntry={locationState?.activeType === 1}
                            />
                        </>
                    )}
                </div>
            </div>

            <FixedBottom className="bg-white">
                <CustomButton
                    onClick={handleActivate}
                    disabled={!canSubmitActivate}
                    loading={activateLoading}
                >
                    {t('确认激活')}
                </CustomButton>
            </FixedBottom>
        </>
    )

    const renderKycEmailReminderPopup = () => (
        <Popup
            visible={kycEmailReminderVisible}
            position="bottom"
            round
            closeable
            closeIcon={<img src="/icon/close.svg" alt="" />}
            onClose={() => setKycEmailReminderVisible(false)}
            style={{ padding: '24px 20px 28px' }}
        >
            <div className="flex flex-col">
                <div className="text-base font-semibold text-[#1D2129] mb-5 pr-8">
                    {t('温馨提示')}
                </div>
                <div className="flex flex-col items-center mb-6">
                    <img
                        src="/icon/kyc-ver-email-icon.svg"
                        alt=""
                        className="w-20 h-20 mb-4"
                    />
                        <div className="text-base text-[#1D2129] text-center leading-relaxed">
                        <Trans
                            i18nKey="验证码已发送至KYC邮箱请查收"
                            components={{
                                kyc: <span className="text-[var(--primary-color)]" />,
                            }}
                        />
                    </div>
                </div>
                <CustomButton onClick={() => setKycEmailReminderVisible(false)}>
                    {t('我知道了')}
                </CustomButton>
            </div>
        </Popup>
    )

    // 激活成功弹窗
    const renderSuccessPopup = () => (
        <Popup
            visible={successPopupVisible}
            round
            position="bottom"
            closeable={false}
            style={{ padding: '40px 20px 20px' }}
        >
            <div className="flex flex-col items-center">
                {/* 成功图标 */}
                <div className="flex flex-col items-center mb-6">
                    <div className="flex items-center justify-center mb-4">
                        <img src="/images/card-v2/recharge-success.svg" alt="" className="w-20 h-20" />
                    </div>
                    <div className="text-7 font-semibold text-[#1D2129]">
                        {t('激活成功')}
                    </div>
                </div>


                {/* 描述 */}
                <div className="text-center mb-8">
                    <div className="text-sm text-[#86909C]">
                        {t('您已成功激活卡片，可前往正常使用')}
                    </div>
                    {boundCardNumber.startsWith('4938') && (
                        <div className="text-base text-[#1D2129] font-medium mt-3">
                            {t('ATM取现初始密码')} <span className="text-[var(--primary-color)]">135246</span>
                        </div>
                    )}
                </div>

                {/* 确定按钮 */}
                <CustomButton onClick={handleSuccessConfirm}>
                    {t('确定')}
                </CustomButton>
            </div>
        </Popup>
    )

    return (
        <PageLayout>
            <Header
                leftChildren={
                    <Back onClick={step === Step.ActivateCard ? handleBack : undefined} />
                }
                centerChildren={
                    <span className="text-base font-medium text-black">
                        {step === Step.BindCard ? t('绑定卡片') : t('激活卡片')}
                    </span>
                }
            />

            {step === Step.BindCard && renderBindCard()}
            {step === Step.ActivateCard && renderActivateCard()}

            {locationState?.activeType === 1 && renderKycEmailReminderPopup()}
            {renderSuccessPopup()}
        </PageLayout>
    )
})
